import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import '../App.css';
import demoVideo from '../assets/videos/Recall-It-Intro.mp4';
import Logo from '../components/Logo';

const { FiBrain, FiUsers, FiEye, FiPlus, FiCheck, FiArrowRight, FiMenu, FiX, FiStar, FiShield, FiZap, FiSun, FiMoon } = FiIcons;

function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    description: '',
    title: '',
    address: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
      
      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
      } else {
        console.warn('Webhook URL not configured');
      }

      console.log('Form submitted:', formData);
      setIsSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', organization: '', description: '', title: '', address: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      // Close modal and reset form even on error for now
      setIsSignupOpen(false);
      setFormData({ firstName: '', lastName: '', email: '', organization: '', description: '', title: '', address: '' });
    }
  };

  const handleCloseModal = () => {
    setIsSignupOpen(false);
    setIsSubmitted(false);
    setFormData({ firstName: '', lastName: '', email: '', organization: '', description: '', title: '', address: '' });
  };

  const features = [
    {
      icon: FiBrain,
      title: "Natural Language Interface",
      description: "Store and retrieve anything using natural language - just speak it, type it, or enter an email or anything that written. "
    },
    {
      icon: FiUsers,
      title: "Collaborative Planning",
      description: "Work together on projects and events with shared memory and planning tools."
    },
    {
      icon: FiZap,
      title: "Smart Organization",
      description: "AI-powered tagging and smart labeling organizes your thoughts automatically."
    },
    {
      icon: FiShield,
      title: "Unified Platform",
      description: "Manage projects, plans, emails, and important papers all in one secure place."
    }
  ];

  const roles = [
    {
      icon: FiBrain,
      title: "Thought Keeper",
      subtitle: "Primary Owner & Organizer",
      benefits: [
        "Your entire memory system is searchable and structured.",
        "You have 100% control over privacy, sharing, and collaboration.",
        "Collaborate with others without giving up ownership.",
        "Create containers to mirror how you think.",
        "Recall effortlessly with semantic recall with English.",
        "Enter thoughts with your voice, typing, scanning, or downloading.",
        "View and contribute to others' user's memory when invited."
      ],
      features: [
        "Capture content through spoken or written language—organized by AI.",
        "Create unlimited containers and sub-containers.",
        "Store and manage all private or sharable thoughts.",
        "Curate incoming ideas from invited contributors.",
        "Change or edit any Thought’s container, security, filters, or tags.",
        "Review and audit every change reviewing a complete version history.",
        "Invite and limit Thought Contributors and Thought Viewers.",
        "Join others' containers as a Contributor or Viewer.",
        "Pay by the level of service you use."
      ]
    },
    {
      icon: FiUsers,
      title: "Thought Contributor",
      subtitle: "Add Ideas & Feedback",
      benefits: [
        "Easily offer ideas, feedback, or memories.",
        "No cost to participate.",
        "Maintain a record of your contributions while still a Contributor and then long after if you become a Thought Keeper."
      ],
      features: [
        "Add thoughts via speech or writing",
        "Retain a personal copy of all suggestions.",
        "Contributions are reviewed by the Thought Keeper before being accepted.",
        "Can upgrade to Thought Keeper to store and manage thoughts and to retain the ideas suggested to others."
      ]
    },
    {
      icon: FiEye,
      title: "Thought Viewer",
      subtitle: "Stay Informed",
      benefits: [
        "Stay up to date about another’s plans and ideas.",
        "Ideal for family, teams, or advisors.",
        "Convert to Thought Keeper to build your own system.",
        "Retain access temporarily after being removed."
      ],
      features: [
        "Free to join and view shareable thoughts.",
        "Cannot submit or edit thoughts",
        "See updates in real-time when shared by the Thought Keeper.",
        "Can upgrade to Thought Keeper to retain or edit your contribution."
      ]
    }
  ];

  const useCases = [
    "Planning and reviewing work projects",
    "Meeting agendas and retreat planning", 
    "Family events, trips, and reunions",
    "Personal journaling and reflection",
    "Goal and habit tracking",
    "Tracking billable hours and expenses",
    "Creating and updating new products",
    "Organizing your thoughts"
  ];

  const roleFeaturesData = [
    { role: "Thought Keeper", features: 10 },
    { role: "Thought Contributor", features: 5 },
    { role: "Thought Viewer", features: 4 }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] overflow-x-hidden selection:bg-yellow-500/30 transition-colors duration-300">
      {/* Background Gradients */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-600/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-600/5 blur-[120px]" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-nav border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <Logo size="small" isDarkMode={isDarkMode} />
            </motion.div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="nav-link hover:text-yellow-400">Features</a>
              <a href="#roles" className="nav-link hover:text-yellow-400">Roles</a>
              <a href="#uses" className="nav-link hover:text-yellow-400">Uses</a>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Toggle theme"
              >
                <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="text-xl text-yellow-400" />
              </button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsSignupOpen(true)}
                className="nav-button px-6 py-2 border-white/10 hover:border-yellow-500/50"
              >
                Get Started
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Toggle theme"
              >
                <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="text-xl text-yellow-400" />
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="nav-button p-2"
              >
                <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl text-yellow-400" />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="md:hidden glass-card mt-2 p-4 border-white/10 bg-black/90"
            >
              <div className="flex flex-col space-y-4">
                <a href="#features" className="nav-link" onClick={() => setIsMenuOpen(false)}>Features</a>
                <a href="#roles" className="nav-link" onClick={() => setIsMenuOpen(false)}>Roles</a>
                <a href="#uses" className="nav-link" onClick={() => setIsMenuOpen(false)}>Uses</a>
                <button onClick={() => { setIsMenuOpen(false); setIsSignupOpen(true); }} className="nav-button px-6 py-2 w-full text-yellow-400">Get Started</button>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="mb-8 flex justify-center items-end gap-8">
                <Logo size="large" isDarkMode={isDarkMode} />
                <img
                  src="/patent-pending-blue.jpg"
                  alt="Patent Pending"
                  className="h-5 w-auto rounded-md opacity-90 hover:opacity-100 transition-opacity"
                  style={{ transform: 'translate(-25px, -15px)' }}
                />
              </div>
              <p className="text-xl md:text-2xl text-[var(--text-secondary)] mb-8 max-w-3xl mx-auto font-light">
                Storing, organizing, and recalling everything you tell it. Assists in critical and creative thinking and collaborative planning.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <button
                onClick={() => setIsSignupOpen(true)}
                className="glass-button-primary px-8 py-4 text-lg flex items-center space-x-2 text-black font-bold"
              >
                <span>Try it now</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
              <button
                onClick={() => setIsVideoOpen(true)}
                className="glass-button px-8 py-4 text-lg hover:border-yellow-500/50 hover:text-yellow-400"
              >
                Watch Demo
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Embed Section */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative aspect-video w-full rounded-xl overflow-hidden shadow-2xl border border-white/10">
            <iframe 
              width="100%" 
              height="100%" 
              src="https://www.youtube.com/embed/ZEQ7zmGzDp0" 
              title="Recall It Demo" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-[var(--text-primary)]">
                Powerful Features
              </span>
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              Organize your ideas, collaborate with others, and remember all important information.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass-card p-6 group hover:bg-white/[0.03] hover:border-yellow-500/30"
              >
                <h3 className="text-xl font-semibold mb-3 text-[var(--text-primary)] group-hover:text-yellow-500 dark:group-hover:text-yellow-200 transition-colors">{feature.title}</h3>
                <p className="text-[var(--text-secondary)]">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-[var(--text-primary)]">
                Three Roles
              </span>
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              Invite others and start collaborating. Each role is designed for different needs and uses
            </p>
          </motion.div>

          {/* Role Cards */}
          <div className="grid lg:grid-cols-3 gap-8">
            {roles.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="glass-card p-8 group hover:border-yellow-500/30"
              >
                
                <h3 className="text-2xl font-bold mb-2 text-[var(--text-primary)]">{role.title}</h3>
                <p className="text-yellow-500 dark:text-yellow-400 text-sm font-medium mb-6">{role.subtitle}</p>
                
                {/* Benefits Section */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Benefits</h4>
                  <div className="space-y-2">
                    {role.benefits.map((benefit, benefitIndex) => (
                      <div key={benefitIndex} className="flex items-start space-x-2">
                        <SafeIcon icon={FiStar} className="text-yellow-500 mt-1 flex-shrink-0 text-sm" />
                        <span className="text-[var(--text-secondary)] text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Features Section */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 text-[var(--text-primary)]">Features</h4>
                  <div className="space-y-2">
                    {role.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start space-x-2">
                        <SafeIcon icon={FiCheck} className="text-yellow-500 mt-1 flex-shrink-0 text-sm" />
                        <span className="text-[var(--text-secondary)] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What is a Thought Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-yellow-500/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-[var(--text-primary)]">
                What is a Thought?
              </span>
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              It is a stored word or phrase and the building block of your<br /> organized ideas to be remembered.


            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Main Definition Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 border-yellow-500/20 hover:border-yellow-500/40 transition-colors lg:col-span-2"
            >
              <h3 className="text-2xl font-bold mb-6 text-yellow-500 dark:text-yellow-400">Core Concept</h3>
              <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-6">
                A <span className="text-[var(--text-primary)] font-semibold">Thought</span> is the smallest meaningful unit stored by Recall It. It is any <br />discrete unit of meaning - simple or profound - from a fleeting idea to a detailed plan.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* What a Thought Can Be */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">A Thought Can Be:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['Idea', 'Name', 'Task', 'Date', 'Memory', 'Note', 'Quote', 'Journal', 'Goal', 'Email', 'Celebration', 'Regret', 'Problem', 'Relationship', 'Action', 'Book', 'Paper', 'Essay'].map((type, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-yellow-500 flex-shrink-0" />
                        <span className="text-[var(--text-secondary)]">{type}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Input Formats */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-[var(--text-primary)]">Input Formats:</h4>
                  <div className="space-y-2">
                    {['Spoken natural language', 'Written natural language', 'Direct text entry'].map((format, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center space-x-2"
                      >
                        <SafeIcon icon={FiCheck} className="text-yellow-500 flex-shrink-0" />
                        <span className="text-[var(--text-secondary)] text-sm">{format}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Thought Properties Cards */}
            {[
              {
                title: 'Single Location',
                description: 'A thought is stored in one container or sub-container'
              },
              {
                title: 'Smart Labels',
                description: 'Multiple tags assigned manually or automatically by AI to each thought'
              },
              {
                title: 'Rich Metadata',
                description: 'Each thought has the contributor, timestamp, emotional tone, urgency, and more'
              },
              {
                title: 'Visibility Control',
                description: 'The Thought Keeper can set it as private or shareable with others'
              },
              {
                title: 'Curation Status',
                description: 'Tracks each thought as Original, Accepted, Modified, or Rejected'
              },
              {
                title: 'Natural Capture',
                description: 'Input via spoken or written natural language'
              }
            ].map((property, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass-card p-6 group hover:border-yellow-500/30"
              >
                <h4 className="text-lg font-semibold mb-2 text-[var(--text-primary)] group-hover:text-yellow-500 transition-colors">
                  {property.title}
                </h4>
                <p className="text-sm text-[var(--text-secondary)]">
                  {property.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="uses" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-[var(--text-primary)]">
                Endless Possibilities
              </span>
            </h2>
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
              From personal journaling to business<br />planning, Recall It adapts to what you want<br /> to remember.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ x: 5 }}
                className="glass-card p-4 flex items-center space-x-3 group hover:border-yellow-500/30 hover:bg-white/[0.03]"
              >
                <SafeIcon icon={FiStar} className="text-yellow-500 flex-shrink-0 group-hover:text-yellow-300 transition-colors duration-300" />
                <span className="text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">{useCase}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-12 text-center border-yellow-500/20 bg-gradient-to-b from-white/[0.02] to-transparent"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[var(--text-primary)]">
              Transform Your Thinking
            </h2>
            <p className="text-xl text-[var(--text-secondary)] mb-8">
              Join others who have already discovered the power of organized thoughts that increase <br />critical and creative thinking and collaborative planning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsSignupOpen(true)}
                className="glass-button-primary px-8 py-4 text-lg flex items-center justify-center space-x-2 text-black font-bold shadow-lg shadow-amber-500/20"
              >
                <span>Try it now</span>
                <SafeIcon icon={FiArrowRight} />
              </button>
              <button onClick={() => setIsVideoOpen(true)} className="glass-button px-8 py-4 text-lg hover:text-yellow-400 hover:border-yellow-500/50">
                Watch Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 px-4 sm:px-6 lg:px-8 border-t border-[var(--glass-border)] bg-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <span className="text-lg font-semibold text-[var(--text-primary)]">Recall It</span>
            </div>
            <div className="flex space-x-6 mb-4 md:mb-0">
                <Link to="/privacy-policy" className="text-[var(--text-secondary)] hover:text-yellow-500 transition-colors">Privacy Policy</Link>
                <Link to="/terms-of-service" className="text-[var(--text-secondary)] hover:text-yellow-500 transition-colors">Terms of Service</Link>
            </div>
            <p className="text-[var(--text-secondary)] text-center">
              © 2025 New Breakthroughs, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Video Popup Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsVideoOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl glass-card p-2 border-yellow-500/30"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsVideoOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
                aria-label="Close video"
              >
                <SafeIcon icon={FiX} className="text-3xl" />
              </button>

              {/* Video Player */}
              <div className="relative aspect-video w-full bg-black rounded-lg overflow-hidden">
                <video
                  controls
                  autoPlay
                  className="w-full h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                  <source src={demoVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Signup Form Popup Modal */}
      <AnimatePresence>
        {isSignupOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {}}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 overflow-hidden"
          >
            <div className="absolute inset-0" onClick={handleCloseModal} />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md glass-card border-yellow-500/30"
              style={{ height: '90vh', maxHeight: '600px' }}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-[var(--text-secondary)] hover:text-yellow-400 transition-colors z-10"
                aria-label="Close form"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>

              {isSubmitted ? (
                <div className="text-center py-8 px-8 h-full flex flex-col justify-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheck} className="text-green-400 text-3xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Thank You!</h3>
                  <p className="text-[var(--text-secondary)] mb-8">
                    Thank you for your interest in Recall It. We'll be in touch soon!
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCloseModal}
                    className="w-full glass-button px-6 py-4 text-lg font-bold text-[var(--text-primary)] hover:text-yellow-400 hover:border-yellow-500/50"
                  >
                    Close
                  </motion.button>
                </div>
              ) : (
                <div className="h-full flex flex-col">
                  {/* Fixed Header */}
                  <div className="flex-shrink-0 px-8 pt-8 pb-4">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-500 via-amber-600 to-yellow-500 dark:from-yellow-200 dark:via-amber-400 dark:to-yellow-200 bg-clip-text text-transparent">
                      Try it now
                    </h2>
                    <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                      This is available now to test for organizations that have at least 5,000 employees.
                    </p>
                  </div>

                  {/* Scrollable Form Content */}
                  <div className="flex-1 overflow-y-auto px-8 pb-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your first name"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your last name"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your email"
                      />
                    </div>

                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Organization
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your organization"
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Description of organization
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        rows="3"
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Describe your organization"
                      />
                    </div>

                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Your title
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your title"
                      />
                    </div>

                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Address
                      </label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-[var(--glass-border)] rounded-lg text-[var(--text-primary)] placeholder-gray-500 focus:outline-none focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/20 transition-all"
                        placeholder="Enter your address"
                      />
                    </div>

                     <motion.button
                       whileHover={{ scale: 1.02 }}
                       whileTap={{ scale: 0.98 }}
                       type="submit"
                       className="w-full glass-button-primary px-6 py-4 text-lg font-bold text-black flex items-center justify-center space-x-2 shadow-lg shadow-amber-500/20"
                     >
                       <span>Try it now</span>
                       <SafeIcon icon={FiArrowRight} />
                     </motion.button>
                   </form>
                 </div>
               </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Home;
